import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { VisitReasonContent as VisitReasonType } from 'types/anamnesis';
import { formatDateToDisplayString, isDateToday, isDateYesterday } from 'utils';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/dropdown/dropdown';
import {
  AnamnesisAccordionContent,
  AnamnesisAccordionItem,
  AnamnesisAccordionTrigger,
  AnamnesisDetails,
  AnamnesisRow,
  VisitReasonBoldText,
  VisitReasonChip,
  VisitReasonChips,
  VisitReasonColoredText,
  VisitReasonContent,
  VisitReasonHeader,
  VisitReasonLine,
  VisitReasonText,
} from './BesuchsgrundSection.styles';
import { buildVisitReasonTextNodes, mapVisitReasonColor } from 'utils/visitReasonDisplay';
import { useSetAnamnesisSeen } from 'hooks';

interface BesuchsgrundSectionProps {
  visitReason?: VisitReasonType;
  availableVisitReasons?: VisitReasonType[];
  onVisitReasonChange?: (visitReason: VisitReasonType) => void;
}

export const BesuchsgrundSection: React.FC<BesuchsgrundSectionProps> = ({
  visitReason,
  availableVisitReasons,
  onVisitReasonChange,
}: BesuchsgrundSectionProps) => {
  const { t } = useTranslation();
  const { mutate: setSeen } = useSetAnamnesisSeen();
  const visitReasonRef = useRef<HTMLDivElement | null>(null);
  const visitReasonDisplayText = (visitReason?.text ?? '').trim();

  useEffect(() => {
    if (visitReason && !visitReason.seen && visitReason.id) {
      setSeen({
        id: visitReason.id,
        data: { seen: true },
      });
    }
  }, [setSeen, visitReason]);

  if (!visitReason) return null;
  const visitReasonText = (visitReason.text ?? '').trim();
  const formattedDate = visitReason.date ? formatDateToDisplayString(visitReason.date) : '';
  const isRelativeDate = visitReason.date
    ? isDateToday(visitReason.date) || isDateYesterday(visitReason.date)
    : false;
  const formattedDateLabel = formattedDate
    ? isRelativeDate
      ? ` ${formattedDate}`
      : ` (${formattedDate})`
    : '';
  const triggerText = `${t('home.anamnesis.visitReason')}${formattedDateLabel}:`;
  const hasVisitReasonContent =
    !!visitReasonText.length || visitReason.chips.length > 0 || visitReason.colored.length > 0;
  const visitReasonNodes = buildVisitReasonTextNodes(
    visitReasonDisplayText,
    visitReason.colored,
    VisitReasonBoldText,
    VisitReasonColoredText
  );

  if (!hasVisitReasonContent) return null;

  const otherVisitReasons = Array.isArray(availableVisitReasons)
    ? availableVisitReasons.filter((vr) => vr.id !== visitReason?.id)
    : [];

  const content = (
    <>
      <AnamnesisDetails>
        <AnamnesisRow>
          <VisitReasonContent>
            <VisitReasonHeader>
              {otherVisitReasons.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="flex items-center gap-1 focus:outline-none">
                      <ChevronDown className="size-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {otherVisitReasons.map((vr) => (
                      <DropdownMenuItem key={vr.id} onClick={() => onVisitReasonChange?.(vr)}>
                        {vr.date ? formatDateToDisplayString(vr.date) : t('general.unknownDate')}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {visitReason.chips.length ? (
                <VisitReasonChips>
                  {visitReason.chips.map((chip, index) => (
                    <VisitReasonChip
                      key={`${chip.text}-${index}`}
                      $color={mapVisitReasonColor(chip.color)}
                    >
                      {chip.text}
                    </VisitReasonChip>
                  ))}
                </VisitReasonChips>
              ) : null}
            </VisitReasonHeader>
            <VisitReasonLine>
              <VisitReasonText aria-label={visitReasonDisplayText} ref={visitReasonRef}>
                {visitReasonNodes}
              </VisitReasonText>
            </VisitReasonLine>
          </VisitReasonContent>
        </AnamnesisRow>
      </AnamnesisDetails>
    </>
  );

  return (
    <AnamnesisAccordionItem value="visitReason">
      <AnamnesisAccordionTrigger>{triggerText}</AnamnesisAccordionTrigger>
      <AnamnesisAccordionContent>{content}</AnamnesisAccordionContent>
    </AnamnesisAccordionItem>
  );
};
